import type { Layer } from "@/types/legend.types";
import type { WithoutID } from "@/types/generic.types";
import type {
  Category,
  Legend,
  LegendSettings,
  NoteConfig,
  ScaleConfig,
} from "@/types/legend.types";
import { LEGEND_VERSION } from "@/utils/constants";
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import { DEFAULT_LEGEND_SETTINGS } from "@/defaultLegend";
import { v4 } from "uuid";
import {
  BY_LAYER_LINEWEIGHT,
  LAYER_DEFAULT_LINEWEIGHT,
} from "@/drawing/lineweight/utils";
import { DEFAULT_FONT } from "@/config/fonts";

export enum Tab {
  GENERAL = "general",
  NOTES = "notes",
  LAYERS = "layers",
  SCALES = "scales",
  PHOTO_MARKERS = "photo_markers",
}

export interface LegendPayload {
  name: string;
  version: string;
  note_configs: NoteConfig[];
  layer_configs: Layer[];
  scale_configs: ScaleConfig[];
  categories: Category[];
  settings: LegendSettings;
}

// Sans id : gabarit pour un nouveau style, l'id (obligatoire sur ScaleConfig) est
// attribué par l'appelant à la création (voir ScalesConfigSection.addScale).
export const defaultScale: Omit<ScaleConfig, "id"> = {
  name: "1-50",
  props: {
    textstyle: {
      name: "1-50",
      size: 0.1,
      filename: DEFAULT_FONT.filename,
    },
  },
};

export const defaultColor = "255,0,0";
// Sans id : c'est un gabarit pour une nouvelle note, l'id (obligatoire sur NoteConfig)
// est attribué par l'appelant à la création (voir NoteCategoryItem.onAddNote).
export const defaultNote: Omit<NoteConfig, "id"> = {
  name: "dashboard:legend.new.defect",
  type: "default",
  label: "N",
  props: {
    model: "ACTIVE",
    color: "kByLayer",
    lineweight: BY_LAYER_LINEWEIGHT,
    linetype: "ByLayer",
  },
};

// Sans id : gabarit pour un nouveau calque, l'id (obligatoire sur Layer) est attribué
// par l'appelant à la création (voir LegendConfigurator.addLayer).
export const defaultLayer: Omit<Layer, "id"> = {
  name: "dashboard:new.layer",
  color: defaultColor,
  visible: true,
  linetype: "Continuous",
  lineweight: LAYER_DEFAULT_LINEWEIGHT,
};

export const useDefaultLegend = () => {
  const { t } = useTranslation();

  const betonCategoryId = v4();
  const crackNoteId = v4();

  return computed<WithoutID<Legend>>(() => ({
    name: t("dashboard:legend.newkey"),
    version: LEGEND_VERSION,
    categories: [{ id: betonCategoryId, name: "Béton", noteIds: [crackNoteId] }],
    note_configs: [
      {
        id: crackNoteId,
        name: t("dashboard:legend.crack"),
        type: "default",
        label: t("dashboard:legend.crack.short"),
        props: {
          model: "ACTIVE",
          color: "kByLayer",
          lineweight: BY_LAYER_LINEWEIGHT,
          linetype: "ByLayer",
        },
      },
      {
        id: v4(),
        name: t("dashboard:legend.photo"),
        type: "photo",
        label: t("dashboard:legend.photo.short"),
        props: {
          model: "ACTIVE",
          color: "kByLayer",
        },
      },
    ],
    layer_configs: [],
    settings: DEFAULT_LEGEND_SETTINGS,
    scale_configs: [
      {
        id: v4(),
        name: "1-50",
        props: {
          textstyle: {
            name: "1-50",
            size: 0.1,
            filename: DEFAULT_FONT.filename,
          },
        },
      },
      {
        id: v4(),
        name: "1-75",
        props: {
          textstyle: {
            name: "1-75",
            size: 0.15,
            filename: DEFAULT_FONT.filename,
          },
        },
      },
      {
        id: v4(),
        name: "1-100",
        props: {
          textstyle: {
            name: "1-100",
            size: 0.2,
            filename: DEFAULT_FONT.filename,
          },
        },
      },
      {
        id: v4(),
        name: "1-125",
        props: {
          textstyle: {
            name: "1-125",
            size: 0.25,
            filename: DEFAULT_FONT.filename,
          },
        },
      },
      {
        id: v4(),
        name: "1-150",
        props: {
          textstyle: {
            name: "1-150",
            size: 0.3,
            filename: DEFAULT_FONT.filename,
          },
        },
      },
      {
        id: v4(),
        name: "1-200",
        props: {
          textstyle: {
            name: "1-200",
            size: 0.4,
            filename: DEFAULT_FONT.filename,
          },
        },
      },
      {
        id: v4(),
        name: "1-250",
        props: {
          textstyle: {
            name: "1-250",
            size: 0.5,
            filename: DEFAULT_FONT.filename,
          },
        },
      },
      {
        id: v4(),
        name: "1-300",
        props: {
          textstyle: {
            name: "1-300",
            size: 0.6,
            filename: DEFAULT_FONT.filename,
          },
        },
      },
    ],
  }));
};

export const legendColors: Array<Array<[number, number, number]>> = [
  // Reds
  [
    [255, 0, 0],
    [204, 0, 0],
    [153, 0, 0],
    [127, 0, 0],
    [76, 0, 0],
  ],
  [
    [255, 63, 0],
    [204, 51, 0],
    [153, 38, 0],
    [127, 31, 0],
    [76, 19, 0],
  ],
  [
    [255, 127, 0],
    [204, 102, 0],
    [153, 76, 0],
    [127, 63, 0],
    [76, 38, 0],
  ],
  [
    [255, 191, 0],
    [204, 153, 0],
    [153, 114, 0],
    [127, 95, 0],
    [76, 57, 0],
  ],
  // Yellows
  [
    [255, 255, 0],
    [204, 204, 0],
    [153, 153, 0],
    [127, 127, 0],
    [76, 76, 0],
  ],
  [
    [191, 255, 0],
    [153, 204, 0],
    [104, 153, 0],
    [95, 127, 0],
    [57, 76, 0],
  ],
  [
    [127, 255, 0],
    [102, 204, 0],
    [76, 153, 0],
    [63, 127, 0],
    [38, 76, 0],
  ],
  [
    [63, 255, 0],
    [51, 204, 0],
    [38, 153, 0],
    [31, 127, 0],
    [19, 76, 0],
  ],
  // Greens
  [
    [0, 255, 0],
    [0, 204, 0],
    [0, 153, 0],
    [0, 127, 0],
    [0, 76, 0],
  ],
  [
    [0, 255, 63],
    [0, 204, 51],
    [0, 153, 38],
    [0, 127, 31],
    [0, 76, 19],
  ],
  [
    [0, 255, 127],
    [0, 204, 102],
    [0, 153, 76],
    [0, 127, 63],
    [0, 76, 38],
  ],
  [
    [0, 255, 191],
    [0, 204, 153],
    [0, 153, 104],
    [0, 127, 95],
    [0, 76, 57],
  ],
  // Cyan
  [
    [0, 255, 255],
    [0, 204, 204],
    [0, 153, 153],
    [0, 127, 127],
    [0, 76, 76],
  ],
  [
    [0, 191, 255],
    [0, 153, 204],
    [0, 104, 153],
    [0, 95, 127],
    [0, 57, 76],
  ],
  [
    [0, 127, 255],
    [0, 102, 204],
    [0, 76, 153],
    [0, 63, 127],
    [0, 38, 76],
  ],
  [
    [0, 63, 255],
    [0, 51, 204],
    [0, 38, 153],
    [0, 31, 127],
    [0, 19, 76],
  ],
  //// Blues
  [
    [0, 0, 255],
    [0, 0, 204],
    [0, 0, 153],
    [0, 0, 127],
    [0, 0, 76],
  ],
  [
    [63, 0, 255],
    [51, 0, 204],
    [38, 0, 153],
    [31, 0, 127],
    [19, 0, 76],
  ],
  [
    [127, 0, 255],
    [102, 0, 204],
    [76, 0, 153],
    [63, 0, 127],
    [38, 0, 76],
  ],
  [
    [191, 0, 255],
    [153, 0, 204],
    [104, 0, 153],
    [95, 0, 127],
    [57, 0, 76],
  ],

  // Purples
  [
    [255, 0, 255],
    [204, 0, 204],
    [153, 0, 153],
    [127, 0, 127],
    [76, 0, 76],
  ],
  [
    [255, 0, 191],
    [204, 0, 153],
    [153, 0, 104],
    [127, 0, 95],
    [76, 0, 57],
  ],
  [
    [255, 0, 127],
    [204, 0, 102],
    [153, 0, 76],
    [127, 0, 63],
    [76, 0, 38],
  ],
  [
    [255, 0, 63],
    [204, 0, 51],
    [153, 0, 38],
    [127, 0, 31],
    [76, 0, 19],
  ],
  // Neutrals and Grays
  [
    [51, 51, 51],
    [91, 91, 91],
    [132, 132, 132],
    [173, 173, 173],
    [255, 255, 255],
  ],
];
